import { api, DATA_BASE } from '../../services/apiClient';

export interface MemberRow {
  account_id: number;
  firstname: string;
  lastname: string;
  dob: string | null;
  phone_prefix: string | null;
  phone_number: string | null;
  role: string;
  active: number;
  // Qué es esta persona según `lib/letsmovePersona.js` del backend (19-sep-2026). En la vista de
  // prospectos dice de qué tipo; en la de alumnas solo importa `prospecto_en_kydemy`.
  persona_tipo?: 'alumna' | 'ex_alumna' | 'visitante' | 'prospecto';
  clases_sueltas?: number;
  ultima_visita?: string | null;
  /** Kydemy la tiene como prospecto PERO está inscrita a una clase vigente: hay que aclararlo. */
  prospecto_en_kydemy?: number;
  has_photo: number;
  membership_id: number | null;
  membership_name: string | null;
  membership_color: string | null;
}

export interface Pagination { page: number; page_size: number; total: number; }

export interface Membership {
  membership_id: number; name: string; code: string | null; color: string | null;
  price_month: string | null; classes_month: number | null;
  default_discount_id: number | null;
}

// (31-ago-2026) `role` se retiró: esta lista es SOLO contacts/alumnos y el backend nunca
// filtró por rol. Profesores = /teachers (users), staff = /usuarios.
/** Confirmar que dos alumnas son familia / sacar a una de su familia (23-sep-2026). */
export function linkFamily(id: number, otra: number) {
  return api.post<{ ok: true }>(`/members/${id}/family`, { contact_id: otra });
}
export function unlinkFamily(id: number) {
  return api.del<{ ok: true }>(`/members/${id}/family`);
}
export function listMembers(params: { search?: string; status?: string; page?: number; page_size?: number }) {
  const q = new URLSearchParams();
  if (params.search) q.set('search', params.search);
  if (params.status) q.set('status', params.status);
  q.set('page', String(params.page ?? 1));
  q.set('page_size', String(params.page_size ?? 20));
  return api.get<{ data: MemberRow[]; pagination: Pagination }>(`/members?${q.toString()}`);
}

export function getMember(id: number) {
  return api.get<{ data: any }>(`/members/${id}`);
}

export function updateMember(id: number, patch: Record<string, unknown>) {
  return api.put<{ ok: true }>(`/members/${id}`, patch);
}

export interface NewMember {
  firstname: string; lastname?: string; email?: string | null;
  phone_prefix?: string | number | null; phone_number?: string | null;
  dob?: string | null; membership_id?: number | null;
  /** Antiduplicados (31-ago-2026): sin esto, mismo teléfono o mismo nombre completo
   *  (activas e inactivas) devuelve 409 con `duplicates`; true = crear de todos modos. */
  force_duplicate?: boolean;
  /** Botón «Clase suelta» de Caja (30-sep-2026): nace PROSPECTO (rol lead), no alumna. */
  prospecto?: boolean;
  notes?: string | null;
}
export interface DuplicateMatch {
  account_id: number; name: string; phone: string | null; is_active: boolean;
  /** Qué es esa persona hoy (30-sep-2026): alumna · ex_alumna · visitante · prospecto · baja. */
  tipo?: 'alumna' | 'ex_alumna' | 'visitante' | 'prospecto' | 'baja';
}
export function createMember(payload: NewMember) {
  return api.post<{ data: { account_id: number } }>(`/members`, payload);
}

export interface MemberClass {
  class_id: number; name: string; color: string | null; style: string | null; teacher: string | null;
  // teacher_id: para abrir la ficha del profesor desde su nombre. schedule: días y horas de la
  // clase, para que la tarjeta se lea sin entrar (Carlos 15-ago).
  teacher_id?: number | null; status?: string | null;
  capacity?: number | null; enrolled?: number | null;
  schedule?: { weekday: number; start_time: string; end_time: string }[];
}
/** Clase de PRUEBA: no hay tabla propia, se deriva de los pagos `voucher_trial` (ver el endpoint).
 *  `class_id`/`color` sólo vienen si el nombre guardado casó con una clase real. */
export interface MemberTrial {
  payment_id: number; date: string; amount: string | number; comment: string | null;
  class_name: string | null; class_id: number | null; color: string | null;
  // 1 = la clase la confirmamos a mano en `letsmove_trial_class` (Kydemy la guardó vacía).
  confirmed?: number;
  // Regla de Karla (15-sep-2026): 'muestra' = gratis ($0, beneficio de alumnos, una por estilo);
  // 'suelta' = clase pagada (entre semana / sábado). type = cómo la etiquetó Kydemy.
  kind: 'muestra' | 'suelta'; type: 'voucher_trial' | 'class'; style: string | null;
}
/** Clase a la que ASISTIÓ sin estar inscrita hoy (sale de las listas de Kydemy). */
export interface MemberPrevious {
  class_id: number; name: string; color: string | null; status: string; style: string | null; teacher: string | null;
  attended: number; first_date: string; last_date: string;
}
export interface MemberClassesResp {
  data: MemberClass[]; trials?: MemberTrial[]; previous?: MemberPrevious[];
  trial_styles?: { style: string; date: string; class_name: string | null }[];
}
export function getMemberClasses(id: number) {
  return api.get<MemberClassesResp>(`/members/${id}/classes`);
}
/** Qué toca cobrar por una clase suelta/muestra: externo paga siempre; alumno con estilo no inscrito
 *  y sin muestra previa → gratis. Es aviso, no candado. */
export interface TrialCheck {
  class_id: number | null; class_name: string | null; style: string | null; saturday: boolean; price: number;
  prices: { weekday: number; saturday: number }; is_student: boolean; enrolled_in_style: boolean;
  /** De dónde salió «sábado»: del horario de la clase o de la fecha del pago (23-sep-2026). */
  saturday_por?: 'clase' | 'clase_semana' | 'fecha';
  muestra_used: { date: string; class_name: string | null } | null; free_available: boolean; charge: number;
  reason: 'externo' | 'inactivo' | 'sin_clase_vigente' | 'muestra_gratis' | 'inscrito_en_estilo' | 'muestra_usada' | 'sin_clase';
}
export function trialCheck(params: { class_id?: number | null; contact_id?: number | null; date?: string }) {
  const q = new URLSearchParams();
  if (params.class_id) q.set('class_id', String(params.class_id));
  if (params.contact_id) q.set('contact_id', String(params.contact_id));
  if (params.date) q.set('date', params.date);
  return api.get<TrialCheck>(`/members/trial-check?${q.toString()}`);
}
export function enrollMemberClass(id: number, classId: number) {
  // already = ya estaba inscrito (el backend no duplica; el que llama decide cómo avisar)
  return api.post<{ ok: true; already?: boolean; suggest_debt?: { month: string; amount: number; plan: string } | null }>(`/members/${id}/classes`, { class_id: classId });
}
export function unenrollMemberClass(id: number, classId: number) {
  return api.del<{ ok: true }>(`/members/${id}/classes/${classId}`);
}

export interface ActiveClass {
  class_id: number; name: string; color: string | null; style: string | null; teacher: string | null;
  capacity?: number | null; level_code?: string | null; enrolled?: number | null;
  schedule?: { weekday: number; start_time: string; end_time: string }[];
}
/** `vigentes` deja fuera las clases activas cuya vigencia ya venció: no tiene sentido ofrecerlas
 *  para inscribir a alguien (el ciclo terminó). Sin la opción, el listado va como siempre. */
export function listActiveClasses(opts?: { vigentes?: boolean }) {
  return api.get<{ data: ActiveClass[] }>(`/classes?status=active${opts?.vigentes ? '&vigencia=vigentes' : ''}`);
}

export function getMemberships() {
  return api.get<{ data: Membership[] }>(`/memberships`);
}

export interface Discount {
  discount_id: number; name: string; code: string | null;
  value: string | number | null; is_percentage: number;
  membership_id: number | null; duration_months: number | null;
  /** Si viene con fecha, el descuento está ARCHIVADO: ya no se ofrece, pero quien lo tiene lo conserva. */
  deleted_at?: string | null;
  /** Ya incluido en el precio del plan («% - 2 Disciplinas»: 2×$700−$100 = $1,300). Se muestra, no se resta. */
  included_in_price?: number;
  /** Descuento de familia (familiares, pareja, mamá + hijo): se sugiere a una posible familia. */
  is_family?: number;
}
/** Otra alumna activa que parece de la misma familia (mismo teléfono o mismos dos apellidos). */
export interface PosibleFamiliar extends FamiliarInfo { razones: string[] }
/** Integrante de la familia (o posible) con lo que pinta su tarjeta/tooltip (API `letsmoveFamilia.enriquecer`). */
export interface FamiliarInfo {
  contact_id: number; nombre: string; activa?: boolean;
  first_name?: string | null; last_name?: string | null; has_photo?: boolean;
  plan?: string | null; descuento?: string | null; descuento_valor?: number | null; descuento_pct?: boolean; descuento_familia?: boolean;
  clases?: { class_id: number; name: string; color: string | null; horario: { weekday: number; start: string }[] }[];
  mes?: string; mes_estado?: 'pagado' | 'parcial' | 'debe' | 'sin_cargo';
}
export function getDiscounts() {
  // all=1: incluye los ARCHIVADOS. Si un alumno tiene asignado uno archivado (ej. 'Dto. Pareja') y la
  // lista no lo trae, el <select> no puede representarlo y al guardar la ficha se le borra sin querer.
  return api.get<{ data: Discount[] }>(`/discounts?all=1`);
}
/** Etiqueta corta del descuento, ej. "−$100" o "−20%". */
export function discountLabel(value: string | number | null, isPct: number | boolean): string {
  const v = Number(value || 0);
  if (!v) return '';
  return isPct ? `−${v}%` : `−$${v.toLocaleString('es-MX')}`;
}

export function age(dob: string | null): string {
  if (!dob) return '—';
  const d = new Date(dob);
  if (isNaN(d.getTime())) return '—';
  const diff = Date.now() - d.getTime();
  return String(Math.floor(diff / (365.25 * 86400000)));
}

export function photoUrl(id: number) { return `${DATA_BASE}/members/${id}/photo`; }

export function uploadMemberPhoto(id: number, image: string) {
  return api.post<{ ok: true }>(`/members/${id}/photo`, { image });
}

export interface MemberPayment {
  payment_id: number; pay_date: string | null; datetime: string | null;
  type: string; method: string; amount: string; assoc_name: string | null;
  months: string | null; invoice_number: string | null;
  /** NULL = capturado aquí (se puede editar/borrar); con valor = importado de Kydemy. */
  kydemy_id: number | null;
  comment: string | null; debt_id: number | null;
  is_partial: number | null; total_expected: string | null;
  /** Descuento aplicado AL COBRO (no el del plan): importe y nombre, ej. '-35% descuento'. */
  amount_discount: string | null; discount_name: string | null;
  /** Cobro en dólares / mixto (3-nov-2026): sin esto «Editar» no sabía que era en USD. */
  currency?: string | null; usd_amount?: string | number | null; fx_rate?: string | number | null; mxn_amount?: string | number | null;
}
export function getMemberPayments(id: number) {
  return api.get<{ data: MemberPayment[]; total: number; count: number }>(`/members/${id}/payments`);
}

export interface Birthday {
  account_id: number; firstname: string; lastname: string;
  dob: string; has_photo: number;
  /** Días hasta el festejo. NEGATIVO = ya pasó (solo si se pidió con `back`); 0 = hoy. */
  days_until: number;
  /** Fecha del festejo que corresponde, ya con su año resuelto (YYYY-MM-DD). */
  bday_date: string;
  role: string; phone_prefix: string | null; phone_number: string | null;
  /** Edad que se cumple EN `bday_date` (no la de hoy: en diciembre no son lo mismo). */
  turns: number; greeted: number;
  classes: string | null; // nombres separados por '|' (pedido Karla: mostrarlas con menor énfasis)
}
/** @param back días hacia atrás a incluir (llegan con days_until negativo). 0 = solo futuro. */
export function getBirthdays(days = 14, back = 0) {
  return api.get<{ data: Birthday[] }>(`/members/birthdays?days=${days}&back=${back}`);
}

export interface BirthdayMonth {
  account_id: number; firstname: string; lastname: string; dob: string;
  has_photo: number; day: number; month: number; turns: number;
  role: string; phone_prefix: string | null; phone_number: string | null;
  greeted: number;
  classes: string | null; // nombres separados por '|' (pedido Karla: mostrarlas con menor énfasis)
}
export function getBirthdaysMonth(month: number) {
  return api.get<{ month: number; year: number; data: BirthdayMonth[] }>(`/members/birthdays?month=${month}`);
}
export function greetMember(id: number) {
  return api.post<{ ok: boolean; greeted: boolean }>(`/members/${id}/greet`);
}

// Alumnos activos cuya última clase TOMADA fue una falta (widget dashboard).
export interface MissedLastClass {
  account_id: number; firstname: string; lastname: string;
  phone_prefix: string | null; phone_number: string | null; has_photo: number;
  class_id: number; class_name: string; color: string | null;
  missed_date: string; days_ago: number;
}
export function getMissedLastClass(days = 30) {
  return api.get<{ data: MissedLastClass[]; summary: { total: number; active_students?: number } }>(`/members/missed-last-class?days=${days}`);
}

// ===== Archivos / documentos del alumno =====
export interface MemberFile {
  file_id: number; original_name: string; mime: string | null; size: number | null;
  created_at: string | null; uploaded_by_name: string | null;
}
export function listMemberFiles(id: number) {
  return api.get<{ data: MemberFile[]; count: number }>(`/members/${id}/files`);
}
/** Sube un archivo crudo (multipart no; bytes directos + nombre en header). */
export async function uploadMemberFile(id: number, file: File) {
  const res = await fetch(`${DATA_BASE}/members/${id}/files`, {
    method: 'POST', credentials: 'include',
    headers: { 'Content-Type': file.type || 'application/octet-stream', 'X-File-Name': encodeURIComponent(file.name) },
    body: file,
  });
  if (!res.ok) { const t = await res.json().catch(() => null); throw new Error((t && t.error) || `HTTP ${res.status}`); }
  return res.json() as Promise<{ ok: true; file_id: number }>;
}
export function deleteMemberFile(id: number, fileId: number) {
  return api.del<{ ok: true }>(`/members/${id}/files/${fileId}`);
}
export function fileDownloadUrl(id: number, fileId: number) {
  return `${DATA_BASE}/members/${id}/files/${fileId}/download`;
}
export function ungreetMember(id: number) {
  return api.del<{ ok: boolean; greeted: boolean }>(`/members/${id}/greet`);
}

export interface MemberAttendance {
  attendance_id: number; status: string | null; date: string | null;
  class_name: string | null; color: string | null;
}
export function getMemberAttendance(id: number) {
  return api.get<{ data: MemberAttendance[]; count: number }>(`/members/${id}/attendance`);
}

// ── Becas (7-oct-2026, Mabel: «botón de becado y no le cobre») ─────────────────────────────────
// La regla de qué mes cubre vive en el backend (lib/letsmoveBecas.js); aquí solo se piden/guardan.
export interface Beca {
  scholarship_id: number; pct: number; covers_membership: boolean; covers_registration: boolean;
  start_date: string; end_date: string; reason: string;
  created_at?: string; created_by_name?: string | null;
  revoked_at?: string | null; revoked_by_name?: string | null; revoke_reason?: string | null;
  vigente?: boolean;
}
export interface BecaInput {
  pct: number; start_date: string; end_date: string;
  covers_membership: boolean; covers_registration: boolean; reason: string;
}
/** Adeudo abierto que la beca perdonaría (cancelar) o bajaría (ajustar, beca parcial). */
export interface BecaAdeudo {
  debt_id: number; debt_type: 'membership' | 'late_fee'; month: string; amount: number; abonado: number;
  comment: string | null; accion: 'cancelar' | 'ajustar'; nuevo: number;
}
export function previewBeca(id: number, body: Partial<BecaInput> & { scholarship_id?: number }) {
  return api.post<{ beca: BecaInput; afectados: BecaAdeudo[] }>(`/members/${id}/becas/preview`, body);
}
export function createBeca(id: number, body: BecaInput & { apply_debt_ids: number[] }) {
  return api.post<{ ok: true; scholarship_id: number; adeudos: BecaAdeudo[] }>(`/members/${id}/becas`, body);
}
export function updateBeca(id: number, bid: number, body: Partial<BecaInput> & { apply_debt_ids: number[] }) {
  return api.put<{ ok: true; adeudos: BecaAdeudo[] }>(`/members/${id}/becas/${bid}`, body);
}
export function revokeBeca(id: number, bid: number, motivo: string) {
  return api.post<{ ok: true }>(`/members/${id}/becas/${bid}/revocar`, { motivo });
}
